import React, { useState } from 'react';

function parseBars(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'N/A';
  const abs = Math.abs(num);
  const prefix = num < 0 ? '-$' : '$';
  if (abs >= 1_000_000_000_000) return `${prefix}${(abs / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `${prefix}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${prefix}${(abs / 1_000_000).toFixed(2)}M`;
  return `${prefix}${abs.toFixed(0)}`;
}

const chartColors = {
  revenue: {
    history: 'bg-blue-500',
    forecast: 'bg-blue-300',
    negativeHistory: 'bg-red-500',
    negativeForecast: 'bg-red-300',
  },
  profit: {
    history: 'bg-green-500',
    forecast: 'bg-green-300',
    negativeHistory: 'bg-red-500',
    negativeForecast: 'bg-red-300',
  },
  cashflow: {
    history: 'bg-emerald-500',
    forecast: 'bg-emerald-300',
    negativeHistory: 'bg-red-500',
    negativeForecast: 'bg-red-300',
  },
  debt: {
    total: '#f06293',
    net: '#00bcd4',
  },
};

function barColorClass(colors, isForecast, isNegative) {
  if (isNegative) return isForecast ? colors.negativeForecast : colors.negativeHistory;
  return isForecast ? colors.forecast : colors.history;
}

/**
 * Percent change vs the prior bar; null when either value is missing or the
 * prior bar is zero. Same formula as the Annual Debt chart's debtYoY.
 */
function pctChange(current, previous) {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

const ChartFallback = ({ title, testId, message = 'Financial history unavailable.' }) => (
  <div className="rounded-lg border border-gray-200 p-4" data-testid={testId}>
    <h4 className="text-sm font-semibold text-gray-800 mb-2">{title}</h4>
    <div className="text-sm text-gray-500">{message}</div>
  </div>
);

const MiniFinancialBarChart = ({ title, rawBars, kind, testId, emptyMessage, showChange = false }) => {
  const bars = parseBars(rawBars).filter((bar) => Number.isFinite(Number(bar.value)));
  if (bars.length === 0) return <ChartFallback title={title} testId={testId} message={emptyMessage} />;
  // Quarterly view shows 8 bars; tighten spacing so value labels still fit.
  const gap = bars.length > 6 ? 'gap-1' : 'gap-3';

  const maxValue = Math.max(...bars.map((bar) => Math.abs(Number(bar.value))), 1);
  const colors = chartColors[kind] || chartColors.revenue;
  const hasForecast = bars.some((bar) => Boolean(bar.forecast));

  return (
    <div className="rounded-lg border border-gray-200 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded ${colors.history}`} />Actual</span>
          {hasForecast && (
            <span className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded ${colors.forecast}`} />Forecast</span>
          )}
        </div>
      </div>

      <div className={`${showChange ? 'h-52' : 'h-48'} flex ${gap}`}>
        {bars.map((bar, index) => {
          const value = Number(bar.value);
          const isForecast = Boolean(bar.forecast);
          const isNegative = value < 0;
          const height = Math.max(8, Math.round((Math.abs(value) / maxValue) * 78));
          const colorClass = barColorClass(colors, isForecast, isNegative);
          // Percent change vs the prior bar — Annual Revenue/Profit only. For
          // revenue and profit, growth UP is good, so the badge colors are the
          // reverse of the Annual Debt chart (where debt up = red).
          const change = showChange && index > 0
            ? pctChange(value, Number(bars[index - 1].value))
            : null;
          return (
            <div key={`${bar.label}-${bar.value}`} className="flex-1 flex flex-col items-center min-w-0">
              {showChange && (
                <div className="h-4 text-[10px] font-semibold leading-none mb-0.5 whitespace-nowrap" data-testid="history-yoy-slot">
                  {change !== null && change !== 0 && (
                    <span
                      data-testid="history-yoy"
                      className={change > 0 ? 'text-green-600' : 'text-red-600'}
                      title={`${change > 0 ? 'Increased' : 'Decreased'} vs ${bars[index - 1].label}`}
                    >
                      {change > 0 ? '▲' : '▼'} {Math.abs(Math.round(change * 100))}%
                    </span>
                  )}
                  {change === 0 && <span className="text-gray-400" data-testid="history-yoy">±0%</span>}
                </div>
              )}
              <div className={`h-5 text-[10px] mb-1 whitespace-nowrap ${isNegative ? 'text-red-600' : 'text-gray-600'}`}>
                {formatMoney(bar.value)}
              </div>
              <div className="relative h-40 w-full flex justify-center">
                <div className="absolute left-0 right-0 top-1/2 border-t border-gray-300" aria-hidden="true" />
                <div
                  data-testid={isForecast ? 'forecast-bar' : 'history-bar'}
                  data-negative={isNegative ? 'true' : 'false'}
                  title={`${bar.label}: ${formatMoney(bar.value)}`}
                  className={`absolute w-full max-w-12 ${isNegative ? 'top-1/2 rounded-b' : 'bottom-1/2 rounded-t'} ${colorClass}`}
                  style={{ height: `${height}px` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={`flex ${gap} mt-2`}>
        {bars.map((bar) => (
          <div key={`${bar.label}-label`} className={`flex-1 text-center text-xs truncate ${bar.forecast ? 'font-semibold text-gray-700' : 'text-gray-500'}`}>
            {bar.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// Debt ÷ Assets leverage thresholds — the same convention as the Debt Ratio
// metric row and the screener's debtRatioFilter (total debt ÷ total assets).
const DEBT_RATIO_LOW = 0.30;
const DEBT_RATIO_HIGH = 0.60;

function debtToAssets(bar) {
  const debt = Number(bar.totalDebt ?? bar.value);
  const assets = Number(bar.totalAssets);
  if (!Number.isFinite(debt) || !Number.isFinite(assets) || assets <= 0) return null;
  return debt / assets;
}

function ratioColorClass(ratio) {
  if (ratio === null || ratio === undefined) return '';
  if (ratio <= DEBT_RATIO_LOW) return 'text-green-600';
  if (ratio > DEBT_RATIO_HIGH) return 'text-red-600';
  return 'text-gray-600';
}

function debtVerdict(ratio) {
  if (ratio === null || ratio === undefined) return null;
  if (ratio <= DEBT_RATIO_LOW) return { label: 'Low', className: 'text-green-700 border-green-200 bg-green-50' };
  if (ratio > DEBT_RATIO_HIGH) return { label: 'High', className: 'text-red-700 border-red-200 bg-red-50' };
  return { label: 'Elevated', className: 'text-amber-700 border-amber-200 bg-amber-50' };
}

/** Year-over-year change of total debt vs the prior bar; null when unknown. */
function debtYoY(bar, previous) {
  if (!previous) return null;
  const debt = Number(bar.totalDebt ?? bar.value);
  const prevDebt = Number(previous.totalDebt ?? previous.value);
  if (!Number.isFinite(debt) || !Number.isFinite(prevDebt) || prevDebt === 0) return null;
  return (debt - prevDebt) / Math.abs(prevDebt);
}

const DebtHistoryChart = ({ rawBars, debtRatioTTM }) => {
  const bars = parseBars(rawBars).filter((bar) => (
    Number.isFinite(Number(bar.totalDebt ?? bar.value)) || Number.isFinite(Number(bar.netDebt))
  ));
  if (bars.length === 0) return <ChartFallback title="Annual Debt" />;

  const maxValue = Math.max(
    ...bars.flatMap((bar) => [Math.abs(Number(bar.totalDebt ?? bar.value) || 0), Math.abs(Number(bar.netDebt) || 0)]),
    1
  );
  const { total, net } = chartColors.debt;
  const latest = bars[bars.length - 1];
  // Header "Debt Ratio" prefers the CURRENT TTM ratio from the stock record
  // (already in the DB), so the chip renders even before the nightly pipeline
  // adds per-year totalAssets; fall back to the latest annual bar's D/A.
  const ttmRatio = Number(debtRatioTTM);
  const hasTtm = Number.isFinite(ttmRatio) && ttmRatio > 0;
  const headerRatio = hasTtm ? ttmRatio : debtToAssets(latest);
  const verdict = debtVerdict(headerRatio);
  // Net cash (latest net debt < 0) lowers risk.
  const netCash = Number(latest.netDebt) < 0;
  const ratioSource = hasTtm
    ? 'current, TTM'
    : `latest annual balance sheet (${latest.label})`;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="annual-debt-chart">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-sm font-semibold text-gray-800 whitespace-nowrap">Annual Debt</h4>
          {verdict && (
            <span
              data-testid="debt-verdict"
              title={`Debt Ratio = total debt ÷ total assets (${ratioSource}). Low ≤30% · Elevated 30–60% · High >60%. Net cash = net debt below zero.`}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${verdict.className}`}
            >
              Debt Ratio {Math.round(headerRatio * 100)}% · {verdict.label}{netCash ? ' · net cash' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: total }} />Total Debt</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: net }} />Net Debt</span>
        </div>
      </div>
      <div className="relative h-44 border-b border-gray-200 border-l border-gray-100 px-2 pt-2">
        {[0, 1, 2].map((tick) => (
          <div
            key={tick}
            className="absolute left-0 right-0 border-t border-gray-100"
            style={{ top: `${18 + tick * 34}%` }}
            aria-hidden="true"
          />
        ))}
        <div className="relative z-10 h-full flex items-end gap-5">
          {bars.map((bar, index) => {
            const totalDebt = Number(bar.totalDebt ?? bar.value) || 0;
            const netDebt = Number(bar.netDebt) || 0;
            const totalHeight = Math.max(4, Math.round((Math.abs(totalDebt) / maxValue) * 132));
            const netHeight = Math.max(4, Math.round((Math.abs(netDebt) / maxValue) * 132));
            const ratio = debtToAssets(bar);
            const change = debtYoY(bar, bars[index - 1]);
            const tooltip = `${bar.label}: Total Debt ${formatMoney(totalDebt)}, Net Debt ${formatMoney(netDebt)}`
              + (ratio !== null ? `, Debt/Assets ${Math.round(ratio * 100)}%` : '');
            return (
              <div key={`${bar.label}-${totalDebt}-${netDebt}`} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full">
                <div className="text-[10px] font-semibold whitespace-nowrap mb-0.5">
                  {change !== null && change !== 0 && (
                    <span
                      data-testid="debt-yoy"
                      className={change > 0 ? 'text-red-600' : 'text-green-600'}
                      title={`Total debt ${change > 0 ? 'increased' : 'decreased'} vs ${bars[index - 1].label}`}
                    >
                      {change > 0 ? '▲' : '▼'} {Math.abs(Math.round(change * 100))}%
                    </span>
                  )}
                  {change === 0 && <span className="text-gray-400" data-testid="debt-yoy">±0%</span>}
                </div>
                <div className="text-[10px] text-gray-600 mb-1 whitespace-nowrap">{formatMoney(totalDebt)}</div>
                <div className="flex items-end justify-center gap-1.5 h-36 w-full" title={tooltip}>
                  <div data-testid="total-debt-bar" className="w-5 rounded-t-sm" style={{ height: `${totalHeight}px`, backgroundColor: total }} />
                  <div data-testid="net-debt-bar" className="w-5 rounded-t-sm" style={{ height: `${netHeight}px`, backgroundColor: net }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-5 mt-2 px-2">
        {bars.map((bar) => {
          const ratio = debtToAssets(bar);
          return (
            <div key={`${bar.label}-debt-label`} className="flex-1 text-center text-xs text-gray-500 min-w-0" title={ratio !== null ? `Debt ÷ Assets for ${bar.label}: total debt ÷ total assets` : undefined}>
              <div className="truncate">{bar.label}</div>
              {ratio !== null && (
                <div className={`text-[10px] font-medium ${ratioColorClass(ratio)}`} data-testid="debt-ratio-label">
                  D/A {Math.round(ratio * 100)}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PERIOD_STORAGE_KEY = 'financial_history_period';
const PERIODS = [
  { value: 'annual', label: 'Annual' },
  { value: 'quarterly', label: 'Quarterly' },
];

function readStoredPeriod() {
  try {
    return window.localStorage.getItem(PERIOD_STORAGE_KEY) === 'quarterly' ? 'quarterly' : 'annual';
  } catch (_) {
    return 'annual';
  }
}

const FinancialHistoryCharts = ({ stock }) => {
  // Remembered across stocks so paging through charts keeps the chosen view.
  const [period, setPeriod] = useState(readStoredPeriod);
  if (!stock) return null;

  const choosePeriod = (next) => {
    setPeriod(next);
    try { window.localStorage.setItem(PERIOD_STORAGE_KEY, next); } catch (_) { /* storage unavailable */ }
  };
  const quarterly = period === 'quarterly';

  return (
    <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Financial History</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span>Revenue &amp; Profit:</span>
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group" aria-label="Revenue and profit period">
              {PERIODS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`financial-period-${option.value}`}
                  aria-pressed={period === option.value}
                  onClick={() => choosePeriod(option.value)}
                  className={`px-3 py-1 text-xs font-medium ${period === option.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {!quarterly && <div className="text-xs text-gray-500">Forecast = avg last 2 quarters × 4</div>}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {quarterly ? (
          <>
            <MiniFinancialBarChart
              title="Quarterly Revenue"
              rawBars={stock.quarterly_revenue_history}
              kind="revenue"
              testId="quarterly-revenue-chart"
              emptyMessage="Quarterly history unavailable."
            />
            <MiniFinancialBarChart
              title="Quarterly Profit"
              rawBars={stock.quarterly_profit_history}
              kind="profit"
              testId="quarterly-profit-chart"
              emptyMessage="Quarterly history unavailable."
            />
          </>
        ) : (
          <>
            <MiniFinancialBarChart
              title="Annual Revenue"
              rawBars={stock.annual_revenue_history}
              kind="revenue"
              testId="annual-revenue-chart"
              showChange
            />
            <MiniFinancialBarChart
              title="Annual Profit"
              rawBars={stock.annual_profit_history}
              kind="profit"
              testId="annual-profit-chart"
              showChange
            />
          </>
        )}
        <DebtHistoryChart rawBars={stock.annual_debt_history} debtRatioTTM={stock.debtRatioTTM} />
        <MiniFinancialBarChart
          title="Annual Free Cash Flow"
          rawBars={stock.annual_free_cash_flow_history}
          kind="cashflow"
          testId="annual-free-cash-flow-chart"
        />
      </div>
    </div>
  );
};

export { parseBars, formatMoney };
export default FinancialHistoryCharts;
